import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Page, PageHeader, Empty } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtDate } from "@/lib/labels";
import { CalendarOff, Plane, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import Personalhandbok from "@/components/Personalhandbok";
import MinSide from "@/components/MinSide";

const todayISO = () => new Date().toISOString().slice(0, 10);

const ABSENCE_TYPES = [
  ["ferie", "Ferie", "bg-blue-500/15 text-blue-300 border-blue-500/30"],
  ["syk", "Syk", "bg-rose-500/15 text-rose-300 border-rose-500/30"],
  ["avspasering", "Avspasering", "bg-violet-500/15 text-violet-300 border-violet-500/30"],
  ["permisjon", "Permisjon", "bg-amber-500/15 text-amber-300 border-amber-500/30"],
  ["hjemmekontor", "Hjemmekontor", "bg-teal-500/15 text-teal-300 border-teal-500/30"],
  ["annet", "Annet", "bg-secondary text-muted-foreground border-border"],
];
const ABS_LABEL = Object.fromEntries(ABSENCE_TYPES.map(([k, l]) => [k, l]));
const ABS_CLS = Object.fromEntries(ABSENCE_TYPES.map(([k, , c]) => [k, c]));

function AbsenceBadge({ type }) {
  return <span className={`text-xs px-2 py-0.5 rounded border ${ABS_CLS[type] || ABS_CLS.annet}`} data-testid={`absence-badge-${type}`}>{ABS_LABEL[type] || type}</span>;
}

function AbsenceTab({ me }) {
  const [absences, setAbsences] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ user_id: "", type: "ferie", date_from: todayISO(), date_to: todayISO(), note: "" });
  const isManager = me && ["admin", "management"].includes(me.role);

  const load = useCallback(() => {
    api.get("/absences?upcoming=true").then((r) => setAbsences(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isManager) api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {}); }, [isManager]);

  const save = async () => {
    if (f.date_to < f.date_from) { toast.error("Til-dato kan ikke være før fra-dato"); return; }
    setSaving(true);
    try {
      await api.post("/absences", {
        user_id: f.user_id || null, type: f.type,
        date_from: f.date_from, date_to: f.date_to,
        note: f.type === "syk" ? "" : f.note,
      });
      toast.success("Fravær registrert");
      setF({ ...f, note: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/absences/${id}`); toast.success("Slettet"); load(); };

  const today = todayISO();
  const active = absences.filter((a) => a.date_from <= today && a.date_to >= today);
  const canManage = (a) => a.user_id === me?.id || isManager;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-display text-lg mb-4">Registrer fravær</h3>
        <div className="space-y-3">
          {isManager && (
            <div>
              <Label className="label-caps">Ansatt</Label>
              <Select value={f.user_id || me?.id} onValueChange={(v) => setF({ ...f, user_id: v })}>
                <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="absence-user-select"><SelectValue placeholder="Velg ansatt" /></SelectTrigger>
                <SelectContent className="bg-popover border-border max-h-72">
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="label-caps">Type</Label>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
              <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="absence-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {ABSENCE_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Fra dato</Label><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value, date_to: e.target.value > f.date_to ? e.target.value : f.date_to })} className="mt-1 bg-secondary border-border" data-testid="absence-from" /></div>
            <div><Label className="label-caps">Til dato</Label><Input type="date" value={f.date_to} min={f.date_from} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="absence-to" /></div>
          </div>
          {f.type === "syk" ? (
            <p className="text-xs text-muted-foreground rounded-lg bg-secondary/50 border border-border p-3" data-testid="absence-sick-notice">
              Ved sykdom registreres kun at du er syk. Detaljer/årsak vises ikke til andre.
            </p>
          ) : (
            <div><Label className="label-caps">Notat (valgfritt)</Label><Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="mt-1 bg-secondary border-border" rows={2} data-testid="absence-note" placeholder="F.eks. tilgjengelig på e-post" /></div>
          )}
          <Button onClick={save} disabled={saving} className="w-full bg-white text-black hover:bg-white/90" data-testid="absence-save-btn"><Plus className="w-4 h-4 mr-1" /> {saving ? "Lagrer…" : "Registrer fravær"}</Button>
        </div>
      </div>

      <div className="lg:col-span-2 space-y-6">
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-display text-lg mb-4 flex items-center gap-2"><CalendarOff className="w-5 h-5 text-rose-300" /> Borte i dag</h3>
          {active.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="absence-none-today">Ingen er registrert borte i dag.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="absence-today-list">
              {active.map((a) => (
                <div key={a.id} className="flex items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm" data-testid={`absence-today-${a.id}`}>
                  <span className="font-medium">{a.user_name}</span>
                  <AbsenceBadge type={a.type} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-display text-lg mb-4 flex items-center gap-2"><Plane className="w-5 h-5 text-blue-300" /> Kommende og pågående fravær</h3>
          {absences.length === 0 ? <Empty icon={CalendarOff} title="Ingen registrert fravær" hint="Registrer fravær i skjemaet til venstre." /> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">{["Ansatt", "Type", "Periode", "Notat", ""].map((h) => <th key={h} className="px-3 py-2 label-caps">{h}</th>)}</tr></thead>
              <tbody>
                {absences.map((a) => (
                  <tr key={a.id} className="border-b border-border/50" data-testid={`absence-row-${a.id}`}>
                    <td className="px-3 py-2 font-medium">{a.user_name}</td>
                    <td className="px-3 py-2"><AbsenceBadge type={a.type} /></td>
                    <td className="px-3 py-2 text-muted-foreground">{fmtDate(a.date_from)}{a.date_to !== a.date_from ? ` – ${fmtDate(a.date_to)}` : ""}</td>
                    <td className="px-3 py-2 text-muted-foreground">{a.type === "syk" ? "—" : (a.note || "—")}</td>
                    <td className="px-3 py-2">{canManage(a) && <button onClick={() => del(a.id)} className="text-muted-foreground hover:text-destructive" data-testid={`absence-del-${a.id}`}><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Ansatt() {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); }, []);

  return (
    <Page>
      <PageHeader title="Ansatt" subtitle="Fravær, min side og personalhåndbok" testid="ansatt-header" />
      <Tabs defaultValue="fravar" className="w-full">
        <TabsList className="bg-secondary/50 border border-border mb-6">
          <TabsTrigger value="fravar" data-testid="tab-fravar">Fravær</TabsTrigger>
          <TabsTrigger value="minside" data-testid="tab-minside">Min side</TabsTrigger>
          <TabsTrigger value="handbok" data-testid="tab-handbok">Personalhåndbok</TabsTrigger>
        </TabsList>
        <TabsContent value="fravar"><AbsenceTab me={me} /></TabsContent>
        <TabsContent value="minside"><MinSide /></TabsContent>
        <TabsContent value="handbok"><Personalhandbok /></TabsContent>
      </Tabs>
    </Page>
  );
}
